import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import api from './axiosInstance.js';
import abroadifyLogo from '../assets/img/logo-large.png';

const getPageWidth = (doc) => doc.internal.pageSize.getWidth();
const getPageHeight = (doc) => doc.internal.pageSize.getHeight();

const loadImage = (src) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });

const addPdfHeader = async (doc) => {
    try {
        const img = await loadImage(abroadifyLogo);
        const maxWidth = 58;
        const maxHeight = 20;
        const imgRatio = img.width / img.height;
        let renderWidth = maxWidth;
        let renderHeight = renderWidth / imgRatio;
        if (renderHeight > maxHeight) {
            renderHeight = maxHeight;
            renderWidth = renderHeight * imgRatio;
        }
        const x = (getPageWidth(doc) - renderWidth) / 2;
        doc.addImage(abroadifyLogo, 'PNG', x, 10, renderWidth, renderHeight);
    } catch {
        doc.setFontSize(18);
        doc.text('ABROADIFY', getPageWidth(doc) / 2, 20, { align: 'center' });
    }
    doc.setDrawColor(192, 217, 238);
    doc.line(14, 32, getPageWidth(doc) - 14, 32);
};

const addPdfFooter = (doc) => {
    const issuingDate = new Date().toLocaleDateString();
    doc.setDrawColor(192, 217, 238);
    const pageHeight = getPageHeight(doc);
    const footerY = pageHeight - 10;
    const lineY = pageHeight - 17;
    const rightX = getPageWidth(doc) - 14;
    doc.line(14, lineY, rightX, lineY);
    doc.setFontSize(9);
    doc.text(`Issuing Date: ${issuingDate}`, 14, footerY);
    doc.textWithLink('app.abroadify.me', rightX, footerY, {
        align: 'right',
        url: 'https://app.abroadify.me',
    });
    doc.setTextColor(0, 0, 0);
};

const addHeaderAndFooterToAllPages = async (doc) => {
    const totalPages = doc.getNumberOfPages();
    for (let page = 1; page <= totalPages; page += 1) {
        doc.setPage(page);
        await addPdfHeader(doc);
        addPdfFooter(doc);
    }
};

const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
    return String(value);
};

const addSectionTable = (doc, title, rows, startY) => {
    const filteredRows = rows.filter(([, value]) => {
        if (value === null || value === undefined) return false;
        if (typeof value === 'boolean') return true;
        if (Array.isArray(value)) return value.length > 0;
        return String(value).trim() !== '';
    });

    if (!filteredRows.length) return startY;

    if (startY > 240) {
        doc.addPage();
        startY = 42;
    }

    doc.setFontSize(13);
    doc.setTextColor(20, 20, 20);
    doc.text(title, 14, startY);
    doc.setTextColor(0, 0, 0);

    autoTable(doc, {
        startY: startY + 4,
        head: [['Field', 'Value']],
        body: filteredRows.map(([label, value]) => [label, formatValue(value)]),
        theme: 'grid',
        styles: {
            fontSize: 9,
            cellPadding: 2.5,
            overflow: 'linebreak',
            valign: 'top',
            lineColor: [192, 217, 238],
            lineWidth: 0.3,
            textColor: [45, 45, 45],
        },
        headStyles: {
            fontSize: 10,
            fillColor: [240, 248, 255],
            textColor: [26, 26, 26],
            lineColor: [192, 217, 238],
            lineWidth: 0.3,
            fontStyle: 'bold',
        },
        bodyStyles: {
            lineColor: [192, 217, 238],
            lineWidth: 0.3,
        },
        alternateRowStyles: {
            fillColor: [255, 255, 255],
        },
        columnStyles: {
            0: { cellWidth: 65 },
            1: { cellWidth: 117 },
        },
        margin: { top: 42, left: 14, right: 14, bottom: 20 },
    });

    return doc.lastAutoTable.finalY + 10;
};

const formatIndustries = (industries = []) => {
    if (!Array.isArray(industries) || !industries.length) return '';
    return industries
        .map((ind, i) => {
            const parts = [
                ind.industry ? `Industry: ${ind.industry}` : '',
                Array.isArray(ind.subIndustry) && ind.subIndustry.length
                    ? `Sub-industry: ${ind.subIndustry.join(', ')}` : '',
                Array.isArray(ind.subIndustry2) && ind.subIndustry2.length
                    ? `Sub-industry 2: ${ind.subIndustry2.join(', ')}` : '',
            ].filter(Boolean);
            return parts.length ? `#${i + 1}: ${parts.join(' | ')}` : null;
        })
        .filter(Boolean)
        .join('\n');
};

export const exportCountryPdf = async (id) => {
    const [countryRes, citiesRes] = await Promise.all([
        api.get(`/api/countries/${id}`),
        api.get(`/api/countries/${id}/cities`),
    ]);

    const d = countryRes.data || {};
    const cities = Array.isArray(citiesRes.data) ? citiesRes.data : [];

    const doc = new jsPDF();
    let y = 42;

    const countryName = d.country?.name || 'Country Export';

    doc.setFontSize(18);
    doc.text(countryName, 14, y);
    y += 8;

    doc.setFontSize(10);
    doc.text(`Exported: ${new Date().toLocaleString()}`, 14, y);
    y += 10;

    // General Info
    y = addSectionTable(doc, 'General Info', [
        ['Country Name',       d.country?.name],
        ['Country ID',         d.country?.id],
        ['ISO2',               d.iso2],
        ['Continent',          d.continent],
        ['Currency',           d.currency],
        ['Number of Citizens', d.citizensNumber],
        ['Languages Spoken',   Array.isArray(d.languagesSpoken) ? d.languagesSpoken.join(', ') : d.languagesSpoken],
        ['Country URL',        d.url],
        ['Activity Status',    d.activityStatus],
    ], y);

    // Visa & Stay
    y = addSectionTable(doc, 'Visa & Stay', [
        ['Visa Required',    d.visaRequired],
        ['Visa Duration',    d.visaDuration],
        ['Visa Type',        d.visaType],
        ['No Permit Period', d.noPermitPeriod],
    ], y);

    // Work & Tax
    y = addSectionTable(doc, 'Work & Tax', [
        ['Weekly Working Hours',          d.weekWorkingHours],
        ['Minimum Wage',                  d.minimumWage],
        ['Wage Amount',                   d.wageAmount],
        ['Internships Regulation',        d.internshipsRegulation],
        ['Income Tax Liability',          d.incomeTaxLiability],
        ['Double Taxation Agreement URL', d.doubleTaxationAgreementUrl],
        ['Health Insurance',              d.healthInsurance],
        ['Accident Insurance',            d.accidentInsurance],
    ], y);

    // Financials & Banks
    y = addSectionTable(doc, 'Financials & Banks', [
        ['Destination Country Currency', d.destinationCountryCurrency],
        ['Bank Account Documents',       d.bankAccountDocuments],
        ['Exchange Rate',                d.exchangeRate],
    ], y);

    // Housing & Life
    y = addSectionTable(doc, 'Housing & Life', [
        ['Average Rental Prices (Urban)', d.averageRentalPricesUrban],
        ['Average Rental Prices (Rural)', d.averageRentalPricesRural],
        ['Monthly Transport Cost',        d.transportMonthly],
        ['Living Costs',                  d.livingCosts],
        ['Renting Restrictions',          d.rentingRestrictions],
        ['Renting Restrictions Details',  d.rentingRestrictionsText],
        ['Platform URLs',                 Array.isArray(d.platformUrls) ? d.platformUrls.join('\n') : d.platformUrls],
    ], y);

    // Social
    y = addSectionTable(doc, 'Social', [
        ['Important Languages',   Array.isArray(d.importantLanguages) ? d.importantLanguages.join(', ') : d.importantLanguages],
        ['English Widely Spoken', d.englishWide],
        ['Social Media Platforms', Array.isArray(d.socialMediaPlatforms) ? d.socialMediaPlatforms.join('\n') : d.socialMediaPlatforms],
        ['Job Platforms',          Array.isArray(d.jobPlatforms) ? d.jobPlatforms.join('\n') : d.jobPlatforms],
    ], y);

    // Industries
    if (Array.isArray(d.industries) && d.industries.length) {
        y = addSectionTable(doc, 'Industries & Career', [
            ['Industries', formatIndustries(d.industries)],
        ], y);
    }

    // Cities table
    if (cities.length === 0) {
        if (y > 240) { doc.addPage(); y = 42; }
        doc.setFontSize(13);
        doc.setTextColor(20, 20, 20);
        doc.text('Cities', 14, y);
        doc.setTextColor(0, 0, 0);
        y += 6;
        doc.setFontSize(10);
        doc.text('No cities have been added for this country yet.', 14, y);
        y += 10;
    } else {
        if (y > 200) { doc.addPage(); y = 42; }

        doc.setFontSize(13);
        doc.setTextColor(20, 20, 20);
        doc.text(`Cities (${cities.length})`, 14, y);
        doc.setTextColor(0, 0, 0);

        autoTable(doc, {
            startY: y + 4,
            head: [['City ID', 'City / Region', 'Country', 'Active']],
            body: cities.map((city) => [
                city.cityId || '—',
                city.name || '—',
                city.country?.name || '—',
                city.activityStatus ? 'Yes' : 'No',
            ]),
            theme: 'grid',
            styles: {
                fontSize: 9,
                cellPadding: 2.5,
                overflow: 'linebreak',
                valign: 'top',
                lineColor: [192, 217, 238],
                lineWidth: 0.3,
                textColor: [45, 45, 45],
            },
            headStyles: {
                fontSize: 10,
                fillColor: [240, 248, 255],
                textColor: [26, 26, 26],
                lineColor: [192, 217, 238],
                lineWidth: 0.3,
                fontStyle: 'bold',
            },
            bodyStyles: {
                lineColor: [192, 217, 238],
                lineWidth: 0.3,
            },
            alternateRowStyles: {
                fillColor: [255, 255, 255],
            },
            columnStyles: {
                0: { cellWidth: 20 },
                1: { cellWidth: 80 },
                2: { cellWidth: 62 },
                3: { cellWidth: 20 },
            },
            margin: { top: 42, left: 14, right: 14, bottom: 20 },
        });
    }

    await addHeaderAndFooterToAllPages(doc);

    const safeFileName = countryName
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-');

    doc.save(`${safeFileName}-country-report.pdf`);
};
